<template>
  <div class="mainfont page-container">
    <v-snackbar v-model="showSnackBar" color="black">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color: white">{{ msg }}</span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showSnackBar = false">
            <v-icon style="color: white">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>
    <v-dialog v-model="confirmDialog.show" max-width="420" persistent>
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-bold">{{ confirmDialog.title }}</v-card-title>
        <v-card-text>{{ confirmDialog.text }}</v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer></v-spacer>
          <v-btn depressed class="btn-act btn-act--neutral" :disabled="confirmDialog.loading" @click="closeConfirm">
            Cancel
          </v-btn>
          <v-btn
            depressed
            class="btn-act"
            :class="confirmDialog.buttonClass"
            :loading="confirmDialog.loading"
            @click="runConfirm"
          >
            {{ confirmDialog.confirmText }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#13736f"
      spinner="spinner"
    />

    <div v-if="!config" class="surface empty-state">
      <v-icon size="48">mdi-lock-outline</v-icon>
      <div class="font-weight-bold" style="color: #334155">This list isn't available for your role.</div>
    </div>

    <template v-else>
      <PageHeader :title="config.title" :subtitle="config.subtitle" :icon="config.icon" :count="totalData" />

      <section class="surface">
        <!-- Filters -->
        <div class="filter-bar">
          <div v-if="!config.noSearch" class="filter-bar-search">
            <v-text-field
              v-model="searchQuery"
              clearable
              dense
              outlined
              hide-details
              placeholder="Search by app no or applicant name"
              background-color="white"
              color="#13736f"
              prepend-inner-icon="mdi-magnify"
              @click:clear="searchQuery = ''"
            ></v-text-field>
          </div>

          <div v-if="config.campFilter">
            <v-autocomplete
              v-model="campId"
              :items="camps"
              item-text="campName"
              item-value="_id"
              clearable
              dense
              outlined
              hide-details
              placeholder="All camps"
              background-color="white"
              color="#13736f"
              prepend-inner-icon="mdi-pine-tree"
            ></v-autocomplete>
          </div>

          <template v-if="config.scope === 'districtCamp'">
            <div>
              <v-autocomplete
                v-model="districtid"
                :items="districts"
                clearable
                dense
                outlined
                hide-details
                placeholder="All districts"
                background-color="white"
                color="#13736f"
                prepend-inner-icon="mdi-map-outline"
              ></v-autocomplete>
            </div>
            <div>
              <v-autocomplete
                v-model="campId"
                :items="camps"
                item-text="campName"
                item-value="_id"
                clearable
                dense
                outlined
                hide-details
                :disabled="!districtid"
                :placeholder="districtid ? 'All camps' : 'Pick a district first'"
                background-color="white"
                color="#13736f"
                prepend-inner-icon="mdi-pine-tree"
              ></v-autocomplete>
            </div>
          </template>

          <div v-if="config.statusFilter">
            <v-select
              v-model="appStatus"
              :items="statusOptions"
              dense
              outlined
              hide-details
              placeholder="All statuses"
              background-color="white"
              color="#13736f"
              prepend-inner-icon="mdi-filter-variant"
            ></v-select>
          </div>

          <div v-if="config.paymentSourceFilter">
            <v-select
              v-model="paymentSource"
              :items="paymentSourceOptions"
              dense
              outlined
              hide-details
              placeholder="All payments"
              background-color="white"
              color="#13736f"
              prepend-inner-icon="mdi-cash-multiple"
            ></v-select>
          </div>

          <div>
            <DateField
              v-model="fromDate"
              dense
              outlined
              hide-details
              placeholder="From date"
              background-color="white"
              icon="mdi-calendar-start"
              icon-position="prepend-inner"
            />
          </div>

          <div>
            <DateField
              v-model="toDate"
              dense
              outlined
              hide-details
              placeholder="To date"
              background-color="white"
              icon="mdi-calendar-end"
              icon-position="prepend-inner"
              :min="fromDate"
            />
          </div>

          <!-- Column headers are hidden on small screens, so sort from here. -->
          <div class="mobile-sort">
            <TableSortSelect v-model="sortKey" :headers="headers" />
          </div>

          <div v-if="config.advancedFilters" class="filter-bar-button">
            <v-btn
              block
              depressed
              class="btn-act btn-act--primary"
              style="height: 40px !important"
              @click="filterDialog = true"
            >
              <v-icon left small>mdi-tune-variant</v-icon>
              Filters
              <span v-if="activeFilterCount > 0" class="filter-count">{{ activeFilterCount }}</span>
            </v-btn>
          </div>

          <div v-if="canBulkDelete" class="filter-bar-button">
            <v-btn
              block
              depressed
              class="btn-act"
              :class="selectMode ? 'btn-act--primary' : 'btn-act--neutral'"
              style="height: 40px !important"
              @click="toggleSelectMode"
            >
              <v-icon left small>{{ selectMode ? "mdi-check" : "mdi-checkbox-multiple-outline" }}</v-icon>
              {{ selectMode ? "Done" : "Select" }}
            </v-btn>
          </div>
        </div>

        <!-- Bulk selection bar (super admin) -->
        <transition name="bulk-bar">
          <div v-if="selectMode" class="bulk-bar" :class="{ 'bulk-bar--active': selectedCount > 0 }">
            <div class="bulk-bar-info">
              <v-icon small :color="selectedCount ? '#b91c1c' : '#64748b'">
                {{ selectedCount ? "mdi-checkbox-marked-circle-outline" : "mdi-gesture-tap" }}
              </v-icon>
              <span v-if="selectedCount" class="bulk-bar-count">
                {{ selectedCount }} application{{ selectedCount === 1 ? "" : "s" }} selected
              </span>
              <span v-else class="bulk-bar-hint">Tick the applications you want to delete.</span>
              <span v-if="selectedCount" class="bulk-bar-hint">Selection is kept when you change pages.</span>
            </div>
            <div class="bulk-bar-actions">
              <v-btn
                v-if="list.length"
                text
                small
                class="text-none font-weight-bold"
                color="#13736f"
                @click="togglePage(!pageAllSelected)"
              >
                {{ pageAllSelected ? "Unselect this page" : "Select this page" }}
              </v-btn>
              <v-btn
                v-if="selectedCount"
                text
                small
                class="text-none font-weight-bold"
                color="#64748b"
                @click="clearSelection"
              >
                Clear
              </v-btn>
              <v-btn
                depressed
                class="btn-act btn-act--sm btn-act--danger"
                :disabled="!selectedCount"
                @click="bulkDelete"
              >
                <v-icon small left>mdi-delete-outline</v-icon>
                Delete{{ selectedCount ? ` (${selectedCount})` : "" }}
              </v-btn>
            </div>
          </div>
        </transition>

        <!-- Active filter chips -->
        <div v-if="activeFilterCount > 0" class="active-filters">
          <span class="active-filters-label">Filtered by</span>
          <v-chip v-if="filters.divisionid" small close class="filter-chip" @click:close="clearFilter('division')">
            <v-icon x-small left>mdi-domain</v-icon>
            {{ filters.divisionName || "Division" }}
          </v-chip>
          <v-chip v-if="filters.rangeid" small close class="filter-chip" @click:close="clearFilter('range')">
            <v-icon x-small left>mdi-map-marker-path</v-icon>
            {{ filters.rangeName || "Range" }}
          </v-chip>
          <v-chip v-if="filters.sectionid" small close class="filter-chip" @click:close="clearFilter('section')">
            <v-icon x-small left>mdi-map-marker-outline</v-icon>
            {{ filters.sectionName || "Section" }}
          </v-chip>
          <v-chip v-if="hasCampType" small close class="filter-chip" @click:close="clearFilter('campType')">
            <v-icon x-small left>mdi-tag-outline</v-icon>
            {{ filters.campType === "free" ? "Free camp" : "Paid camp" }}
          </v-chip>
          <v-btn text x-small color="#dc2626" class="font-weight-bold text-none" @click="resetFilters">
            Clear all
          </v-btn>
        </div>

        <!-- Table: sorting happens on the server so it covers every page. -->
        <div v-if="list.length > 0" class="table-scroll">
          <v-data-table
            class="app-table"
            :headers="headers"
            :items="list"
            item-key="_id"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :server-items-length="totalData"
            :mobile-breakpoint="0"
            hide-default-footer
          >
            <template v-slot:header.select>
              <v-simple-checkbox
                :value="pageAllSelected"
                :indeterminate="pageSomeSelected && !pageAllSelected"
                color="#13736f"
                :ripple="false"
                @input="togglePage"
              ></v-simple-checkbox>
            </template>
            <template v-slot:item="{ item }">
              <tr :class="{ 'row--selected': selectMode && isSelected(item) }">
                <td v-if="selectMode" data-label="Select" class="select-cell">
                  <v-simple-checkbox
                    :value="isSelected(item)"
                    color="#b91c1c"
                    :ripple="false"
                    @input="toggleItem(item)"
                  ></v-simple-checkbox>
                </td>
                <td data-label="Application">
                  <div>
                    <span class="app-no">{{ item.applicationNo || item.applicationId || item._id }}</span>
                    <div v-if="item.created_at" class="cell-muted mt-1">Applied {{ formatDate(item.created_at) }}</div>
                  </div>
                </td>

                <td data-label="Camp">
                  <div>
                    <div class="cell-strong text-capitalize">{{ item.campid ? item.campid.campName : "N/A" }}</div>
                    <div v-if="item.campid && item.campid.divisionid && item.campid.divisionid.officeName" class="cell-muted">
                      <v-icon x-small color="#94a3b8">mdi-map-marker-outline</v-icon>
                      {{ item.campid.divisionid.officeName }}
                    </div>
                    <span
                      v-if="item.campid && item.campid.campType"
                      class="camp-type"
                      :class="item.campid.campType === 'free' ? 'camp-type--free' : 'camp-type--paid'"
                    >
                      {{ item.campid.campType === "free" ? "Free camp" : "Paid camp" }}
                    </span>
                  </div>
                </td>

                <td data-label="Applicant">
                  <div>
                    <div class="cell-strong">{{ item.applicantname || "N/A" }}</div>
                    <div v-if="item.userid && item.userid.mobNumber" class="cell-muted">
                      <v-icon x-small color="#94a3b8">mdi-phone-outline</v-icon>
                      {{ item.userid.mobNumber }}
                    </div>
                    <div v-if="item.district" class="cell-muted">
                      <v-icon x-small color="#94a3b8">mdi-map-marker-outline</v-icon>
                      {{ item.district }}
                    </div>
                    <div v-if="categoryText(item)" class="cell-muted category-line">
                      <v-icon x-small color="#13736f">mdi-shape-outline</v-icon>
                      {{ categoryText(item) }}
                    </div>
                  </div>
                </td>

                <td data-label="Camp date">
                  <div>
                    <div class="cell-strong">
                      <v-icon small color="#13736f">mdi-calendar-blank-outline</v-icon>
                      {{ formatDate(item.startDate) }}
                    </div>
                    <div v-if="item.campDuration" class="cell-muted">
                      {{ item.campDuration }} day{{ item.campDuration == 1 ? "" : "s" }}
                    </div>
                  </div>
                </td>

                <td data-label="Status">
                  <div class="status-stack">
                    <div class="d-flex align-center flex-wrap" style="gap: 6px">
                      <span class="status-pill" :class="'status-pill--' + statusInfo(item).tone">
                        {{ statusInfo(item).label }}
                      </span>
                      <span
                        v-if="listPayment(item)"
                        class="status-pill"
                        :class="'status-pill--' + listPayment(item).tone"
                      >
                        {{ listPayment(item).label }}
                      </span>
                      <span v-if="item.memberCount" class="member-count">
                        <v-icon x-small color="#475569">mdi-account-group-outline</v-icon>
                        {{ item.memberCount }}
                      </span>
                    </div>
                    <div v-if="cancelReason(item)" class="reason-note">
                      <v-icon x-small color="#b91c1c">mdi-message-alert-outline</v-icon>
                      {{ cancelReason(item) }}
                    </div>
                    <div
                      v-if="showCurrentlyWith && getCurrentlyWith(item)"
                      class="currently-with"
                      :style="getCurrentlyWithBadgeStyle(item)"
                    >
                      <v-icon x-small :color="getCurrentlyWithIconColor(item)">{{ getCurrentlyWithIcon(item) }}</v-icon>
                      <span :style="getCurrentlyWithTextStyle(item)">{{ getCurrentlyWith(item) }}</span>
                    </div>
                  </div>
                </td>

                <td class="app-table-actions text-right">
                  <div class="row-actions">
                    <template v-for="(action, a) in rowActions(item)">
                      <v-btn
                        v-if="action.type === 'view'"
                        :key="a"
                        depressed
                        class="btn-act btn-act--sm btn-act--primary"
                        @click="openDetail(item)"
                      >
                        <v-icon small left>mdi-eye-outline</v-icon>
                        {{ action.label || "View" }}
                      </v-btn>
                      <!-- Forward: pick the destination from a dropdown -->
                      <v-menu
                        v-else-if="action.type === 'forward'"
                        :key="a"
                        offset-y
                        left
                        min-width="240"
                        @input="(open) => open && loadRowTargets(item)"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            depressed
                            class="btn-act btn-act--sm btn-act--amber"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon small left>mdi-share-outline</v-icon>
                            {{ forwardHasRevert ? "Forward / Revert" : "Forward" }}
                            <v-icon small right>mdi-chevron-down</v-icon>
                          </v-btn>
                        </template>
                        <div class="forward-menu-body">
                          <div class="forward-menu-title">{{ forwardHasRevert ? "Forward or revert to" : "Forward to" }}</div>
                          <div v-if="rowTargetState(item).loading" class="forward-menu-state">
                            <v-progress-circular indeterminate size="16" width="2" color="#13736f"></v-progress-circular>
                            Loading…
                          </div>
                          <div v-else-if="!rowTargetState(item).users.length" class="forward-menu-state">
                            No officer is set up to receive this.
                          </div>
                          <template v-else>
                            <button
                              v-for="target in rowTargetState(item).users"
                              :key="target.userId"
                              type="button"
                              class="forward-menu-item"
                              @click="openForward(item, target)"
                            >
                              <v-icon small :color="target.revert ? '#b45309' : '#13736f'">{{
                                target.revert ? "mdi-undo-variant" : target.person ? "mdi-account-outline" : "mdi-briefcase-account-outline"
                              }}</v-icon>
                              <span>{{ target.revert ? "Revert to " : "Forward to " }}{{ target.name }}</span>
                            </button>
                          </template>
                        </div>
                      </v-menu>
                      <v-btn
                        v-else-if="action.type === 'timeline'"
                        :key="a"
                        depressed
                        class="btn-act btn-act--sm btn-act--info"
                        @click="openTimeline(item)"
                      >
                        <v-icon small left>mdi-timeline-clock-outline</v-icon>
                        Timeline
                      </v-btn>
                      <v-btn
                        v-else-if="action.type === 'download'"
                        :key="a"
                        depressed
                        class="btn-act btn-act--sm btn-act--doc"
                        @click="download(action, item._id)"
                      >
                        <v-icon small left>{{ action.icon }}</v-icon>
                        {{ action.label }}
                      </v-btn>
                      <v-btn
                        v-else-if="action.type === 'markPaid'"
                        :key="a"
                        depressed
                        class="btn-act btn-act--sm btn-act--primary"
                        @click="markPaid(item)"
                      >
                        <v-icon small left>{{ action.icon }}</v-icon>
                        {{ action.label }}
                      </v-btn>
                      <v-btn
                        v-else-if="action.type === 'deleteApp'"
                        :key="a"
                        depressed
                        class="btn-act btn-act--sm btn-act--danger"
                        @click="deleteApp(item)"
                      >
                        <v-icon small left>{{ action.icon }}</v-icon>
                        {{ action.label }}
                      </v-btn>
                    </template>
                  </div>
                </td>
              </tr>
            </template>
          </v-data-table>
        </div>

        <!-- Empty states -->
        <div v-else-if="!appLoading" class="empty-state">
          <v-icon size="48">{{ config.icon }}</v-icon>
          <div class="font-weight-bold" style="color: #334155">{{ config.emptyText }}</div>
          <div style="font-size: 13px">Try a different search, date range or filter.</div>
        </div>

        <!-- Pagination -->
        <div v-if="pages > 1" class="pagination-bar">
          <v-pagination
            v-model="currentPage"
            :length="pages"
            :total-visible="$vuetify.breakpoint.xsOnly ? 5 : 7"
            prev-icon="mdi-chevron-left"
            next-icon="mdi-chevron-right"
            color="#13736f"
          ></v-pagination>
        </div>
      </section>
    </template>

    <ForwardDialog
      v-if="config && config.forward"
      v-model="forwardDialog"
      :application="forwardItem"
      :setup="config.forward"
      :target="forwardTarget"
      @notify="notify"
      @done="getList"
    />

    <ApplicationFilterDialog
      v-if="config && config.advancedFilters"
      v-model="filterDialog"
      :userRole="role"
      :initialFilters="filters"
      @apply="onApplyFilters"
      @reset="resetFilters"
    />

    <ApplicationTimelineDialog
      v-model="timelineDialog"
      :appId="selectedTimelineAppId"
      :application="selectedTimelineApp"
    />
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import PageHeader from "@/components/Common/PageHeader";
import ApplicationFilterDialog from "@/components/Common/ApplicationFilterDialog.vue";
import ApplicationTimelineDialog from "@/components/Common/ApplicationTimelineDialog.vue";
import ForwardDialog from "@/components/Common/ForwardDialog.vue";
import TableSortSelect from "@/components/Common/TableSortSelect.vue";
import serverSortMixin from "@/components/Common/serverSortMixin";
import { loadForwardTargets, setupHasRevert } from "@/components/Common/forwardTargets";
import { getUserType } from "@/components/Common/navConfig";
import { getListConfig, DELETE_APP } from "@/components/Common/applicationListConfig";

// The server accepts at most this many applications per bulk delete.
const BULK_DELETE_LIMIT = 200;
import {
  getCurrentlyWith,
  getCurrentlyWithBadgeStyle,
  getCurrentlyWithTextStyle,
  getCurrentlyWithIcon,
  getCurrentlyWithIconColor,
  paymentBadge,
} from "@/utils/applicationHelper";

const DISTRICTS = [
  "Thiruvananthapuram",
  "Kollam",
  "Pathanamthitta",
  "Alappuzha",
  "Kottayam",
  "Idukki",
  "Ernakulam",
  "Thrissur",
  "Palakkad",
  "Malappuram",
  "Kozhikode",
  "Wayanad",
  "Kannur",
  "Kasaragod",
];

const STATUS_OPTIONS = [
  { text: "All statuses", value: "all" },
  { text: "Pending", value: "pending" },
  { text: "Approved", value: "approved" },
  { text: "Rejected", value: "rejected" },
  { text: "Cancelled", value: "cancelled" },
];

const PAYMENT_SOURCE_OPTIONS = [
  { text: "All payments", value: "all" },
  { text: "Namudekeralam", value: "namudekeralam" },
  { text: "Direct (other)", value: "direct" },
];

// Categories the DFO finalises without CWW (super admin) review, so their approval
// is shown as "Approved by DFO". Must match the backend's applicantCategory values.
const DFO_FINAL_CATEGORIES = [
  "Students",
  "Corporation Councilors,Muncipal Councilors,Jilla Panchayat Members,Block Panchayat Members And Grama Panchayt Members",
];

// Confirmation dialog used before row actions; the action runs only on confirm.
function closedConfirm() {
  return { show: false, loading: false, title: "", text: "", confirmText: "", buttonClass: "", run: null };
}

function emptyFilters() {
  return {
    divisionid: null,
    divisionName: "",
    rangeid: null,
    rangeName: "",
    sectionid: null,
    sectionName: "",
    campType: "",
  };
}

export default {
  name: "ApplicationList",
  components: {
    PageHeader,
    ApplicationFilterDialog,
    ApplicationTimelineDialog,
    ForwardDialog,
    TableSortSelect,
  },
  mixins: [serverSortMixin],
  props: {
    listType: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      appLoading: false,
      msg: null,
      showSnackBar: false,
      list: [],
      totalData: 0,
      pages: 0,
      currentPage: 1,
      searchQuery: "",
      searchTimeout: null,
      // Status filter (config.statusFilter lists); ?status=pending etc. preselects it.
      appStatus: STATUS_OPTIONS.some((o) => o.value === this.$route.query.status) ? this.$route.query.status : "all",
      statusOptions: STATUS_OPTIONS,
      paymentSource: "all",
      paymentSourceOptions: PAYMENT_SOURCE_OPTIONS,
      confirmDialog: closedConfirm(),
      fromDate: null,
      toDate: null,
      fromMenu: false,
      toMenu: false,
      filters: emptyFilters(),
      filterDialog: false,
      // district / camp scoping (admin + division pending)
      districts: DISTRICTS,
      districtid: null,
      camps: [],
      campId: null,
      // dialogs
      timelineDialog: false,
      selectedTimelineAppId: null,
      selectedTimelineApp: null,
      forwardDialog: false,
      forwardItem: null,
      forwardTarget: null,
      // Forward destinations per application, loaded when its menu opens.
      rowTargets: {},
      // Bulk delete: selected applications by id -> application number (kept across pages).
      selectMode: false,
      selected: {},
    };
  },
  computed: {
    forwardHasRevert() {
      return setupHasRevert(this.config.forward);
    },
    role() {
      return getUserType();
    },
    config() {
      return getListConfig(this.listType, this.role);
    },
    hasCampType() {
      return !!this.filters.campType && this.filters.campType !== "all";
    },
    activeFilterCount() {
      let count = 0;
      if (this.filters.divisionid) count++;
      if (this.filters.rangeid) count++;
      if (this.filters.sectionid) count++;
      if (this.hasCampType) count++;
      return count;
    },
    // Bulk select is offered wherever the row Delete action is (super admin lists).
    canBulkDelete() {
      return !!this.config && this.config.actions.includes(DELETE_APP);
    },
    selectedCount() {
      return Object.keys(this.selected).length;
    },
    pageSomeSelected() {
      return this.list.some((item) => this.isSelected(item));
    },
    pageAllSelected() {
      return this.list.length > 0 && this.list.every((item) => this.isSelected(item));
    },
    headers() {
      const select = this.selectMode ? [{ text: "", value: "select", sortable: false, width: 48 }] : [];
      return [
        ...select,
        { text: "Application", value: "applicationNo" },
        { text: "Camp", value: "campid", sortable: false },
        { text: "Applicant", value: "applicantname" },
        { text: "Camp date", value: "startDate" },
        { text: "Status", value: "status", sortable: false },
        { text: "Actions", value: "actions", sortable: false, align: "end" },
      ];
    },
    // Pending lists only hold applications already at the user's seat, so the
    // "With: …" badge would just repeat the current user.
    showCurrentlyWith() {
      return this.config && this.config.status === "auto";
    },
  },
  watch: {
    searchQuery() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => this.reload(), 350);
    },
    currentPage() {
      this.getList();
    },
    appStatus() {
      this.reload();
    },
    paymentSource() {
      this.reload();
    },
    fromDate() {
      this.reload();
    },
    toDate() {
      this.reload();
    },
    districtid() {
      this.campId = null;
      this.camps = [];
      this.reload();
    },
    campId() {
      if (this.config && (this.config.scope === "districtCamp" || this.config.campFilter)) this.reload();
    },
  },
  mounted() {
    if (!this.config) return;
    if (this.config.campFilter) this.getDivisionCamps();
    this.getList();
  },
  methods: {
    getCurrentlyWith,
    getCurrentlyWithBadgeStyle,
    getCurrentlyWithTextStyle,
    getCurrentlyWithIcon,
    getCurrentlyWithIconColor,

    notify(message) {
      this.msg = message;
      this.showSnackBar = true;
    },
    formatDate(value) {
      if (!value) return "N/A";
      return moment(value).format("DD MMM YYYY");
    },

    // ---------- list ----------
    // Filters/search changed (or a delete finished): rows that are no longer listed
    // must not stay selected, so the selection starts over. Paging keeps it.
    reload() {
      this.clearSelection();
      if (this.currentPage !== 1) {
        this.currentPage = 1; // the currentPage watcher reloads
      } else {
        this.getList();
      }
    },
    getList() {
      if (!this.config) return;
      const search = this.searchQuery && this.searchQuery.trim() ? this.searchQuery.trim() : "";
      const { url, data } = this.config.request({
        page: this.currentPage,
        fromDate: this.fromDate,
        toDate: this.toDate,
        search,
        appStatus: this.appStatus,
        paymentSource: this.paymentSource,
        filters: this.filters,
        districtid: this.districtid,
        campId: this.campId,
        camp: this.camps.find((c) => c._id === this.campId) || {},
      });
      Object.assign(data, this.sortParams());

      this.appLoading = true;
      axios({
        method: "POST",
        url,
        headers: {
          token: localStorage.getItem("token"),
        },
        data,
      })
        .then((response) => {
          const res = response.data || {};
          if (res.status !== false && Array.isArray(res.data)) {
            this.list = res.data;
            this.rowTargets = {};
            this.totalData = res.totalLength || res.data.length;
            this.pages = Math.ceil(this.totalData / (res.limit || data.limit || 10));
            // Admin pending: the district call also returns that district's camps.
            if (this.config.scope === "districtCamp" && !this.campId && Array.isArray(res.camps)) {
              this.camps = res.camps.map((camp) => ({ _id: camp._id, campName: camp.campName }));
            }
          } else {
            this.list = [];
            this.totalData = 0;
            this.pages = 0;
            if (res.msg) this.notify(res.msg);
          }
          this.appLoading = false;
        })
        .catch((err) => {
          this.list = [];
          this.totalData = 0;
          this.pages = 0;
          this.appLoading = false;
          this.notify("Couldn't load applications. Please try again.");
          console.error(err);
        });
    },
    // Camps for the DFO's camp filter
    getDivisionCamps() {
      axios({
        method: "POST",
        url: "/division/naturecamp",
        headers: {
          token: localStorage.getItem("token"),
        },
        data: { page: 1, limit: 100 },
      })
        .then((response) => {
          this.camps = (response.data && response.data.data) || [];
        })
        .catch((err) => {
          this.notify("Couldn't load your camps for the filter.");
          console.error(err);
        });
    },
    // ---------- filters ----------
    onApplyFilters(newFilters) {
      this.filters = { ...emptyFilters(), ...newFilters };
      this.reload();
    },
    resetFilters() {
      this.filters = emptyFilters();
      this.reload();
    },
    clearFilter(key) {
      if (key === "division") {
        this.filters = { ...this.filters, divisionid: null, divisionName: "", rangeid: null, rangeName: "", sectionid: null, sectionName: "" };
      } else if (key === "range") {
        this.filters = { ...this.filters, rangeid: null, rangeName: "", sectionid: null, sectionName: "" };
      } else if (key === "section") {
        this.filters = { ...this.filters, sectionid: null, sectionName: "" };
      } else if (key === "campType") {
        this.filters = { ...this.filters, campType: "" };
      }
      this.reload();
    },

    // ---------- row display ----------
    statusInfo(item) {
      const approvedLabel = DFO_FINAL_CATEGORIES.includes(item.applicantCategory) ? "Approved by DFO" : "Approved";
      switch (this.config.status) {
        case "pending":
          return { label: "Pending", tone: "warning" };
        case "approved":
          return { label: approvedLabel, tone: "success" };
        case "cancelled":
          return { label: "Cancelled", tone: "danger" };
        case "cancelRequest":
          return { label: "Cancel requested", tone: "warning" };
        default:
          break;
      }
      if (item.status === "Cancelled" || item.cancelRequest === "Yes") return { label: "Cancelled", tone: "danger" };
      if (item.superAdminApprovalStatus === "Success") return { label: approvedLabel, tone: "success" };
      if (item.divisonApprovalStatus === "rejected" || item.superAdminApprovalStatus === "rejected")
        return { label: "Rejected", tone: "danger" };
      if (this.config.statusFilter && item.finalSubmit === "true" && item.superAdminApprovalStatus === "Pending")
        return { label: "Pending", tone: "warning" };
      if (item.status === "Active" || !item.status) return { label: "In progress", tone: "info" };
      return { label: item.status, tone: "neutral" };
    },
    // Only show payment once it is due (after approval) or done.
    listPayment(item) {
      const badge = paymentBadge(item);
      return badge && badge.tone !== "neutral" ? badge : null;
    },
    cancelReason(item) {
      if (this.config.status !== "cancelled") return "";
      return item.rejectReasonadmin || item.rejectReasondfo || "";
    },
    rowActions(item) {
      return this.config.actions.filter((action) => !action.when || action.when(item));
    },
    categoryText(item) {
      const category = item.applicantCategory || item.studentCategory;
      if (!category) return "";
      return item.applicantCategory === "Students" && item.studentCategory
        ? `${category} (${item.studentCategory})`
        : category;
    },

    // ---------- row actions ----------
    openDetail(item) {
      this.$router.push(this.config.detail(item._id));
    },
    openTimeline(item) {
      this.selectedTimelineAppId = item._id || item.applicationId;
      this.selectedTimelineApp = item;
      this.timelineDialog = true;
    },
    download(action, appid) {
      const headers =
        action.auth === "bearer"
          ? { Authorization: `Bearer ${localStorage.getItem("token")}` }
          : { token: localStorage.getItem("token") };
      axios
        .get(action.url, { headers, params: { appid }, responseType: "blob" })
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", action.fileName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch((error) => {
          this.notify("Couldn't download the document. Please try again.");
          console.error("Error downloading document:", error);
        });
    },
    // ---------- confirm dialog ----------
    askConfirm(options) {
      this.confirmDialog = { ...closedConfirm(), ...options, show: true };
    },
    closeConfirm() {
      this.confirmDialog = closedConfirm();
    },
    runConfirm() {
      const dialog = this.confirmDialog;
      this.confirmDialog = { ...dialog, loading: true };
      dialog
        .run()
        .then((data) => {
          this.closeConfirm();
          this.notify(data.msg);
          if (data.status) this.reload();
        })
        .catch((error) => {
          this.confirmDialog = { ...this.confirmDialog, loading: false };
          this.notify(dialog.errorMsg);
          console.error(dialog.errorMsg, error);
        });
    },
    markPaid(item) {
      this.askConfirm({
        title: "Mark as paid",
        text: `Mark the payment for application ${item.applicationNo} as paid?`,
        confirmText: "Mark as paid",
        buttonClass: "btn-act--primary",
        errorMsg: "Couldn't mark the payment. Please try again.",
        run: () =>
          axios
            .post("/superadmin/mark/paid", { appid: item._id }, { headers: { token: localStorage.getItem("token") } })
            .then((response) => response.data),
      });
    },
    deleteApp(item) {
      this.askConfirm({
        title: "Delete application",
        text: `Delete application ${item.applicationNo} completely? This cannot be undone.`,
        confirmText: "Delete",
        buttonClass: "btn-act--danger",
        errorMsg: "Couldn't delete the application. Please try again.",
        run: () =>
          axios
            .post("/superadmin/delete/application", { appid: item._id }, { headers: { token: localStorage.getItem("token") } })
            .then((response) => response.data),
      });
    },

    // ---------- bulk select / delete ----------
    toggleSelectMode() {
      this.selectMode = !this.selectMode;
      if (!this.selectMode) this.clearSelection();
    },
    isSelected(item) {
      return Object.prototype.hasOwnProperty.call(this.selected, item._id);
    },
    toggleItem(item) {
      if (this.isSelected(item)) {
        this.$delete(this.selected, item._id);
      } else {
        this.$set(this.selected, item._id, item.applicationNo || item._id);
      }
    },
    togglePage(value) {
      this.list.forEach((item) => {
        if (value) this.$set(this.selected, item._id, item.applicationNo || item._id);
        else this.$delete(this.selected, item._id);
      });
    },
    clearSelection() {
      this.selected = {};
    },
    bulkDelete() {
      const ids = Object.keys(this.selected);
      if (!ids.length) return;
      if (ids.length > BULK_DELETE_LIMIT) {
        this.notify(`You can delete at most ${BULK_DELETE_LIMIT} applications at a time.`);
        return;
      }
      const numbers = Object.values(this.selected);
      const shown = numbers.slice(0, 5).join(", ");
      const more = numbers.length > 5 ? ` and ${numbers.length - 5} more` : "";
      const count = ids.length === 1 ? "1 application" : `${ids.length} applications`;
      this.askConfirm({
        title: `Delete ${count}`,
        text: `Delete ${count} (${shown}${more}) completely, along with their payment records and history? This cannot be undone.`,
        confirmText: `Delete ${count}`,
        buttonClass: "btn-act--danger",
        errorMsg: "Couldn't delete the selected applications. Please try again.",
        run: () =>
          axios
            .post("/superadmin/delete/applications", { appids: ids }, { headers: { token: localStorage.getItem("token") } })
            .then((response) => response.data),
      });
    },

    // ---------- forward ----------
    rowTargetState(item) {
      return this.rowTargets[item._id] || { loading: true, users: [] };
    },
    loadRowTargets(item) {
      const cached = this.rowTargets[item._id];
      if (cached && !cached.loading) return;
      this.$set(this.rowTargets, item._id, { loading: true, users: [] });
      loadForwardTargets(this.config.forward, item._id)
        .then((users) => {
          this.$set(this.rowTargets, item._id, { loading: false, users });
        })
        .catch((err) => {
          this.$set(this.rowTargets, item._id, { loading: false, users: [] });
          this.notify("Couldn't load the officers to forward to.");
          console.log(err);
        });
    },
    openForward(item, target) {
      this.forwardItem = item;
      this.forwardTarget = target || null;
      this.forwardDialog = true;
    },
  },
};
</script>

<style scoped>
.app-table td[data-label="Application"] {
  width: 160px;
}

.app-table td[data-label="Status"] {
  min-width: 200px;
}

.forward-menu-body {
  padding: 6px;
  background: #ffffff;
}

.forward-menu-title {
  padding: 6px 10px 4px;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #64748b;
}

.forward-menu-state {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  font-size: 13px;
  color: #64748b;
}

.forward-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 8px;
  background: none;
  text-align: left;
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  cursor: pointer;
}

.forward-menu-item:hover,
.forward-menu-item:focus-visible {
  background: rgba(19, 115, 111, 0.08);
  outline: none;
}

.reason-note {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  max-width: 260px;
  padding: 6px 8px;
  border-radius: 8px;
  background: #fef2f2;
  color: #7f1d1d;
  font-size: 12px;
  line-height: 1.35;
}

/* ---------- Bulk select ---------- */
.bulk-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin: 0 16px 12px;
  padding: 10px 12px 10px 14px;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  background: #f8fafc;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.bulk-bar--active {
  border-style: solid;
  border-color: #fecaca;
  background: #fef2f2;
}

.bulk-bar-info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  min-width: 0;
}

.bulk-bar-count {
  font-size: 14px;
  font-weight: 800;
  color: #7f1d1d;
}

.bulk-bar-hint {
  font-size: 12px;
  color: #64748b;
}

.bulk-bar-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.bulk-bar-enter-active,
.bulk-bar-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.bulk-bar-enter,
.bulk-bar-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.app-table td.select-cell {
  width: 48px;
  padding-right: 0 !important;
}

.app-table tbody tr.row--selected,
.app-table tbody tr.row--selected:hover {
  background: #fef2f2 !important;
}

@media (max-width: 768px) {
  .app-table tbody tr.row--selected {
    border-color: #fca5a5;
  }

  .app-table td.select-cell {
    width: 100%;
    align-items: center;
  }
}

@media (max-width: 600px) {
  .reason-note {
    max-width: 100%;
  }

  .bulk-bar {
    margin: 0 12px 12px;
  }

  .bulk-bar-actions {
    width: 100%;
  }

  .bulk-bar-actions .btn-act {
    flex: 1 1 auto;
  }
}
</style>
